import React, { useEffect, useState } from 'react';
import BaseModal from './BaseModal';
import ImportValidationStep from '../components/ImportValidationStep';
import './LoadExportModal.css';
import { useSetup } from '../context/SetupContext';
import { base64ToImage, renderVerbatimTranslation, normalizeVerbatimLink } from '../utils/VerbatimUtils';
import { extractDomainInfo } from '../utils/URLUtils';
import { stripDefectiveVerbatims, validateImportedProjectStructure } from '../utils/ImportValidationUtils';
import PropTypes from 'prop-types';

const cloneData = (data) => {
    if (data === undefined || data === null) return {};
    return JSON.parse(JSON.stringify(data));
};

const ensureSourcesLabel = (projectData, defaultLabels, defaultLabelTypes) => {
    const clonedData = cloneData(projectData);
    const preparedData = clonedData && typeof clonedData === 'object' && !Array.isArray(clonedData)
        ? clonedData
        : {};
    const rawSourceLabels = Array.isArray(preparedData.labels?.Sources)
        ? [...preparedData.labels.Sources]
        : Array.isArray(defaultLabels?.Sources)
            ? [...defaultLabels.Sources]
            : [];
    const sourceLabels = rawSourceLabels
        .filter((source) => source && typeof source === 'object' && !Array.isArray(source))
        .map((source) => ({
            ...source,
            keywords: Array.isArray(source.keywords) ? [...source.keywords] : [],
        }));

    const verbatims = Array.isArray(preparedData.verbatims) ? preparedData.verbatims : [];
    const currentLabelTypes = preparedData.labelTypes && typeof preparedData.labelTypes === 'object'
        ? preparedData.labelTypes
        : {};

    if (!currentLabelTypes.Sources) {
        preparedData.labelTypes = {
            ...currentLabelTypes,
            Sources: defaultLabelTypes?.Sources || { mandatory: true, sentimentEnabled: false, multiValue: false },
        };
    }

    verbatims.forEach((verbatim) => {
        try {
            const linkValue = normalizeVerbatimLink(verbatim?.link);
            const { hostname, hostnameWithoutTld, baseName, baseNameFormatted } = extractDomainInfo(linkValue?.trim() || '');

            if (!hostname && !hostnameWithoutTld && !baseName && !baseNameFormatted) return;

            const existingSourceByName = sourceLabels.find((source) => source?.name?.toLowerCase() === baseName);
            const existingSourceInKeywords = sourceLabels.find(
                (source) => Array.isArray(source.keywords) && source.keywords.some((keyword) => keyword === hostnameWithoutTld)
            );

            if (baseNameFormatted && !existingSourceByName && !existingSourceInKeywords) {
                sourceLabels.push({ name: baseNameFormatted, keywords: [hostnameWithoutTld], explanation: '' });
            } else if (hostnameWithoutTld && existingSourceByName && !existingSourceByName.keywords.includes(hostnameWithoutTld)) {
                existingSourceByName.keywords.push(hostnameWithoutTld);
            }

            if (!verbatim.labels) {
                verbatim.labels = {};
            }

            if (!verbatim.labels.Sources) {
                let sourceName = '';
                if (existingSourceByName) sourceName = existingSourceByName.name;
                else if (existingSourceInKeywords) sourceName = existingSourceInKeywords.name;
                else sourceName = baseNameFormatted;

                verbatim.labels.Sources = [{ name: sourceName, state: 'active', type: 'Sources' }];
            }
        } catch (error) {
            console.error(`Lien invalide : ${verbatim?.link}`, error);
        }
    });

    preparedData.labels = {
        ...(preparedData.labels || {}),
        Sources: sourceLabels,
    };

    preparedData.labelTypes = Object.fromEntries(
        Object.entries(preparedData.labelTypes || {}).map(([key, value]) => [key.trim(), value])
    );

    preparedData.labels = Object.fromEntries(
        Object.entries(preparedData.labels || {}).map(([key, value]) => [
            key.trim(),
            Array.isArray(value)
                ? value.map((label) => {
                    const nextLabel = { ...label };
                    if (nextLabel.name) nextLabel.name = nextLabel.name.trim();
                    return nextLabel;
                })
                : [],
        ])
    );

    preparedData.verbatims = verbatims.map((verbatim) => {
        const nextVerbatim = { ...verbatim };

        if (nextVerbatim.labels) {
            nextVerbatim.labels = Object.fromEntries(
                Object.entries(nextVerbatim.labels).map(([key, value]) => [
                    key.trim(),
                    Array.isArray(value)
                        ? value.map((label) => {
                            const nextLabel = { ...label };
                            if (nextLabel.name) nextLabel.name = nextLabel.name.trim();
                            if (nextLabel.type) nextLabel.type = nextLabel.type.trim();
                            return nextLabel;
                        })
                        : [],
                ])
            );
        }

        return nextVerbatim;
    });

    return preparedData;
};

const LoadExportModal = ({ isOpen, onClose, loadedData, onConfirm }) => {
    const { labels: existingLabels, defaultLabels, defaultLabelTypes } = useSetup();
    const [step, setStep] = useState(1);
    const [action, setAction] = useState(null);
    const [selectedLabels, setSelectedLabels] = useState({});
    const [openSections, setOpenSections] = useState({});
    const [selectedSlides, setSelectedSlides] = useState({});
    const [notesBySlide, setNotesBySlide] = useState({});
    const [workingData, setWorkingData] = useState(null);
    const [validationReport, setValidationReport] = useState(null);
    const [activeValidationTab, setActiveValidationTab] = useState('warnings');

    useEffect(() => {
        if (!loadedData) {
            setStep(1);
            setAction(null);
            setSelectedLabels({});
            setOpenSections({});
            setSelectedSlides({});
            setNotesBySlide({});
            setWorkingData(null);
            setValidationReport(null);
            return;
        }

        const preparedData = ensureSourcesLabel(loadedData, defaultLabels, defaultLabelTypes);
        const validation = validateImportedProjectStructure(preparedData);
        const normalizedData = cloneData(validation.normalizedData);

        setStep(1);
        setAction(null);
        setSelectedLabels({});
        setOpenSections({});
        setSelectedSlides({});
        setNotesBySlide({});
        setWorkingData(normalizedData);
        setValidationReport({
            ...validation,
            normalizedData,
        });
        setActiveValidationTab(validation.errors.length > 0 ? 'errors' : 'warnings');
    }, [loadedData, existingLabels, defaultLabels, defaultLabelTypes]);

    useEffect(() => {
        if (!workingData || !action) return;

        const initialLabels = {};
        const workingLabels = workingData.labels || {};

        if (action === 'append') {
            Object.keys(workingLabels).forEach((labelType) => {
                const newLabels = workingLabels[labelType]
                    .filter((label) => !existingLabels[labelType]?.some((existingLabel) => existingLabel.name === label.name))
                    .map((label) => label.name);

                if (newLabels.length > 0) {
                    initialLabels[labelType] = newLabels;
                }
            });
        } else if (action === 'overwrite') {
            Object.keys(workingLabels).forEach((labelType) => {
                initialLabels[labelType] = (workingLabels[labelType] || []).map((label) => label.name);
            });
        }

        setSelectedLabels(initialLabels);

        const groupedNotes = {};
        (workingData.notes || []).forEach((note) => {
            const slideId = note.slideId;
            if (!groupedNotes[slideId]) {
                groupedNotes[slideId] = [];
            }
            groupedNotes[slideId].push(note);
        });
        setNotesBySlide(groupedNotes);

        const initialSlides = {};
        (workingData.slides || []).forEach((slide) => {
            initialSlides[slide.id] = { selected: true, notes: {} };

            (groupedNotes[slide.id] || []).forEach((note) => {
                initialSlides[slide.id].notes[note.id] = true;
            });
        });
        setSelectedSlides(initialSlides);
    }, [workingData, action, existingLabels]);

    const toggleCheckbox = (stateSetter, key, subKey) => {
        stateSetter((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                ...(subKey
                    ? { [subKey]: !prev[key]?.[subKey] }
                    : { selected: !prev[key]?.selected }),
            },
        }));
    };

    const handleSlideCheckboxChange = (slideId) => toggleCheckbox(setSelectedSlides, slideId);
    const handleNoteCheckboxChange = (slideId, noteId) => toggleCheckbox(setSelectedSlides, slideId, noteId);

    const handleCheckboxChange = (labelType, labelName) => {
        setSelectedLabels((prev) => {
            const updatedLabels = { ...prev };

            if (updatedLabels[labelType]?.includes(labelName)) {
                updatedLabels[labelType] = updatedLabels[labelType].filter((label) => label !== labelName);
            } else {
                updatedLabels[labelType] = [...(updatedLabels[labelType] || []), labelName];
            }

            return updatedLabels;
        });
    };

    const handleActionSelection = (selectedAction) => {
        setAction(selectedAction);
        setStep(2);
    };

    const defectiveCount = validationReport?.defectiveVerbatimIndexes?.length || 0;
    const hasDefectiveVerbatims = defectiveCount > 0;

    const handleValidationPrimary = () => {
        if (validationReport?.normalizedData) {
            setWorkingData(cloneData(validationReport.normalizedData));
        }
        setStep(3);
    };

    const handleValidationSecondary = () => {
        if (hasDefectiveVerbatims && validationReport) {
            const sanitizedData = stripDefectiveVerbatims(
                validationReport.normalizedData,
                validationReport.defectiveVerbatimIndexes
            );
            setWorkingData(sanitizedData);
            setStep(3);
            return;
        }

        setStep(1);
    };

    const handleConfirm = () => {
        if (!workingData) return;

        const finalLabels = Object.keys(selectedLabels).reduce((result, labelType) => {
            result[labelType] = (workingData.labels?.[labelType] || []).filter((label) =>
                selectedLabels[labelType]?.includes(label.name)
            );
            return result;
        }, {});

        const selectedLabelTypes = Object.keys(selectedLabels).reduce((result, labelType) => {
            result[labelType] = workingData.labelTypes?.[labelType];
            return result;
        }, {});

        const selectedSlideData = (workingData.slides || [])
            .filter((slide) => selectedSlides[slide.id]?.selected)
            .map((slide) => ({
                ...slide,
                notes: notesBySlide[slide.id]?.filter((note) => selectedSlides[slide.id].notes[note.id]) || [],
            }));

        onConfirm({
            action,
            selectedLabels: finalLabels,
            selectedLabelTypes,
            selectedSlides: selectedSlideData,
            projectData: workingData,
        });

        setStep(1);
    };

    const getModalTitle = (currentStep) => {
        const titles = [
            'Load Type',
            'Import Validation',
            'Load Type > Label Selection',
            'Load Type > Label Selection > Verbatims Review',
            'Load Type > Label Selection > Verbatims Review > Slides Selection & Confirm',
        ];

        return titles[currentStep - 1] || 'Load Export';
    };

    if (!isOpen) return null;

    const currentVerbatims = workingData?.verbatims || [];
    const currentLabels = workingData?.labels || {};
    const currentSlides = workingData?.slides || [];

    return (
        <BaseModal
            show={isOpen}
            title={getModalTitle(step)}
            onClose={onClose}
            primaryActionLabel={
                step === 2
                    ? hasDefectiveVerbatims
                        ? 'Continue with all'
                        : 'Continue'
                    : step === 5
                        ? `Confirm ${action?.charAt(0).toUpperCase() + action?.slice(1)}`
                        : 'Next'
            }
            onConfirm={
                step === 2
                    ? handleValidationPrimary
                    : step === 5
                        ? handleConfirm
                        : step === 1
                            ? null
                            : () => setStep((prev) => prev + 1)
            }
            secondaryActionLabel={
                step === 2
                    ? hasDefectiveVerbatims
                        ? 'Continue without defective'
                        : 'Back'
                    : step > 1
                        ? 'Back'
                        : 'Cancel'
            }
            onSecondaryAction={
                step === 2
                    ? hasDefectiveVerbatims
                        ? handleValidationSecondary
                        : () => setStep(1)
                    : step > 1
                        ? () => setStep((prev) => prev - 1)
                        : onClose
            }
        >
            <div className="loadexportmodal-content-wrapper">
                {step === 1 && (
                    <>
                        <h2>Select Action</h2>
                        <p>Choose how to load the data:</p>
                        <div className="loadexportmodal-action-box-container">
                            <div className="loadexportmodal-action-box" onClick={() => handleActionSelection('append')}>
                                <h3>Append</h3>
                                <p>Adds new items from the loaded file to the current project. Existing items will be kept.</p>
                            </div>
                            <div className="loadexportmodal-action-box" onClick={() => handleActionSelection('overwrite')}>
                                <h3>Overwrite</h3>
                                <p>Replaces the current project data with data from the loaded file. Existing items will be removed.</p>
                            </div>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <ImportValidationStep
                        validationReport={validationReport}
                        activeTab={activeValidationTab}
                        onTabChange={setActiveValidationTab}
                    />
                )}

                {step === 3 && (
                    <>
                        <h2>Select Labels to {action === 'append' ? 'Append' : 'Overwrite'}</h2>
                        <p>{action === 'append' ? 'Select labels found in the export file to add to the set of labels that already exist in the current project.' : 'Review labels to be loaded from the file.'}</p>
                        <div className="loadexportmodal-collapsible-container">
                            {Object.keys(selectedLabels).map((labelType) => (
                                <div key={labelType} className="loadexportmodal-collapsible-section">
                                    <button
                                        className="loadexportmodal-collapsible-header"
                                        onClick={() => setOpenSections((prev) => ({ ...prev, [labelType]: !prev[labelType] }))}
                                    >
                                        {labelType}
                                    </button>
                                    {openSections[labelType] && (
                                        <div className="loadexportmodal-collapsible-content">
                                            <table className="loadexportmodal-table">
                                                <thead>
                                                    <tr>
                                                        <th>Select</th>
                                                        <th>Name</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(currentLabels[labelType] || []).map((label) => (
                                                        <tr key={label.name}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedLabels[labelType]?.includes(label.name) || false}
                                                                    onChange={() => handleCheckboxChange(labelType, label.name)}
                                                                />
                                                            </td>
                                                            <td>{label.name}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {step === 4 && (
                    <>
                        <h2>
                            Preview Verbatims to{' '}
                            {action === 'append' ? 'append to existing project' : 'overwrite the existing project'}
                        </h2>
                        <p>20 verbatims out of {currentVerbatims.length} present in the export are displayed</p>
                        <div className="loadexportmodal-verbatim-container">
                            <table className="loadexportmodal-verbatim-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Verbatim</th>
                                        <th>Translation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentVerbatims.slice(0, 20).map((verbatim, index) => (
                                        <tr key={index}>
                                            <td>{verbatim.id}</td>
                                            <td>
                                                {typeof verbatim.verbatim === 'string' && verbatim.verbatim}
                                                {Array.isArray(verbatim.verbatim) && (
                                                    <div className="verbatim-blocks-preview">
                                                        {verbatim.verbatim.map((item, itemIndex) => {
                                                            if (item.type === 'text' && item.content.trim() !== '') {
                                                                return (
                                                                    <span key={itemIndex} className="verbatim-block-chip text">
                                                                        {item.content}
                                                                    </span>
                                                                );
                                                            }

                                                            if (item.type === 'image' && item.content) {
                                                                return (
                                                                    <span key={itemIndex} className="verbatim-block-chip image">
                                                                        <img
                                                                            src={base64ToImage(item.content)}
                                                                            alt="Verbatim"
                                                                            className="review-verbatim-image compact"
                                                                            onError={(e) => {
                                                                                console.error('Erreur de chargement de l\'image', e);
                                                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==';
                                                                            }}
                                                                        />
                                                                    </span>
                                                                );
                                                            }

                                                            return null;
                                                        })}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{renderVerbatimTranslation(verbatim.translation)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {step === 5 && (
                    <>
                        <h2>Select Slides and Notes to {action === 'append' ? 'Append' : 'Overwrite'}</h2>
                        <p>Select the slides and corresponding notes to include:</p>
                        <div className="loadexportmodal-collapsible-container">
                            {currentSlides.map((slide) => (
                                <div key={slide.id} className="loadexportmodal-collapsible-section">
                                    <button
                                        className="loadexportmodal-collapsible-header"
                                        onClick={() => setOpenSections((prev) => ({ ...prev, [slide.id]: !prev[slide.id] }))}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSlides[slide.id]?.selected || false}
                                            onChange={() => handleSlideCheckboxChange(slide.id)}
                                        />
                                        <span>{slide.title}</span>
                                    </button>
                                    {openSections[slide.id] && (
                                        <ul className="loadexportmodal-notes-list">
                                            {(notesBySlide[slide.id] || []).map((note) => (
                                                <li key={note.id}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSlides[slide.id]?.notes[note.id] || false}
                                                        onChange={() => handleNoteCheckboxChange(slide.id, note.id)}
                                                    />
                                                    <span>{note.content}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </BaseModal>
    );
};

LoadExportModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    loadedData: PropTypes.shape({
        labels: PropTypes.object,
        labelTypes: PropTypes.object,
        verbatims: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                verbatim: PropTypes.oneOfType([
                    PropTypes.string.isRequired,
                    PropTypes.arrayOf(
                        PropTypes.shape({
                            content: PropTypes.string.isRequired,
                        })
                    ),
                ]),
            })
        ),
        slides: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                title: PropTypes.string.isRequired,
            })
        ),
        notes: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                slideId: PropTypes.number.isRequired,
                content: PropTypes.string.isRequired,
            })
        ),
    }),
    onConfirm: PropTypes.func.isRequired,
};

export default LoadExportModal;
